/**
 * Shared legal document content.
 * Used in LegalGate (acceptance flow) and LegalDocumentPage (view/print later).
 */
import React from 'react';

export const CURRENT_LEGAL_VERSION = '1.0';

// ─── Terms of Service ─────────────────────────────────────────────────────────

export function TermsOfService() {
  return (
    <div className="space-y-6 text-gray-700 text-sm leading-relaxed">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Terms of Service — PM Review</h2>
        <p className="text-gray-500 text-xs mt-1">Version {CURRENT_LEGAL_VERSION} · Effective April 2026</p>
      </div>

      <p>
        These Terms of Service ("Terms") form a legally binding agreement between <strong>J Parker Software</strong>
        {' '}(a sole trader, hereinafter "we", "us" or "the Provider") and the school or educational institution
        whose authorised representative accepts these Terms ("you" or "the School").
      </p>
      <p>
        By clicking "Accept", the individual accepting confirms they have authority to enter into this agreement
        on behalf of the School.
      </p>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">1. The Service</h3>
        <p>
          PM Review is a cloud-based performance management platform designed for UK schools. It provides
          tools for creating review cycles, managing staff objectives, completing self-reviews, recording
          line manager assessments, and generating reports. The service is provided via the web at
          <strong> pmreview.co.uk</strong> and any associated domains.
        </p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">2. Subscription and Pricing</h3>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            The subscription fee is <strong>£1.00 (one pound sterling) per active staff member per calendar month</strong>,
            exclusive of VAT (where applicable).
          </li>
          <li>
            An "active staff member" means any user account within your school that has <strong>isActive: true</strong>
            {' '}at any point during that calendar month. Deactivated accounts do not count from the date of deactivation.
          </li>
          <li>
            Invoices are issued monthly in arrears based on your highest active staff count during that month.
            Payment is due within 30 days of invoice date.
          </li>
          <li>
            A <strong>30-day free trial</strong> is provided from the date your school account is first activated.
            No payment is due during the trial period.
          </li>
          <li>
            We reserve the right to change pricing with a minimum of <strong>30 days' written notice</strong> by email.
            Continued use of the service after the notice period constitutes acceptance of the new pricing.
          </li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">3. Term and Termination</h3>
        <ul className="list-disc pl-5 space-y-2">
          <li>This agreement runs on a rolling monthly basis from the date of acceptance.</li>
          <li>
            Either party may terminate this agreement by giving <strong>30 days' written notice</strong> by email
            to the other party. Notice from the School should be sent to{' '}
            <a href="mailto:hello@pmreview.co.uk" className="text-brand-600 hover:underline">hello@pmreview.co.uk</a>.
          </li>
          <li>
            We may terminate immediately if the School materially breaches these Terms and fails to remedy
            the breach within 14 days of written notice.
          </li>
          <li>
            Upon termination, the School's data will be retained for 30 days to allow export, after which
            it will be permanently deleted.
          </li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">4. Acceptable Use</h3>
        <p>The School agrees to:</p>
        <ul className="list-disc pl-5 space-y-2 mt-2">
          <li>Use the service only for lawful school performance management purposes</li>
          <li>
            Ensure all staff are informed that the system is being used for performance management
            in accordance with the School's employment policies and applicable law
          </li>
          <li>Not share login credentials between users or attempt to circumvent access controls</li>
          <li>Not use the service to store special category personal data (as defined by UK GDPR) without a documented lawful basis</li>
          <li>Promptly notify us of any security incidents or suspected unauthorised access</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">5. Data Ownership</h3>
        <p>
          All data entered into the platform by the School or its staff remains the property of the School.
          We do not claim ownership of any School data. We process data solely as a data processor acting
          on the School's instructions — see the Data Processing Agreement for full details.
        </p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">6. Availability and Support</h3>
        <p>
          We aim to keep the service available and will make reasonable efforts to maintain availability,
          perform updates outside of school hours, and address reported issues promptly. However, as a
          small provider, we do not offer a formal Service Level Agreement (SLA) or guaranteed uptime.
          Support is provided by email at{' '}
          <a href="mailto:hello@pmreview.co.uk" className="text-brand-600 hover:underline">hello@pmreview.co.uk</a>.
        </p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">7. Limitation of Liability</h3>
        <p>
          To the fullest extent permitted by law, our total liability to the School for any claim arising
          under or in connection with these Terms (whether in contract, tort, or otherwise) shall not
          exceed the total fees paid by the School in the <strong>three calendar months</strong> immediately
          preceding the event giving rise to the claim.
        </p>
        <p className="mt-2">
          We are not liable for any indirect, consequential, or special loss, including loss of data,
          loss of profits, or loss of opportunity, even if we have been advised of the possibility of
          such losses.
        </p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">8. Intellectual Property</h3>
        <p>
          The PM Review platform, its software, design, and all associated intellectual property rights
          remain the sole property of J Parker Software. No licence to copy, distribute, or modify the
          software is granted beyond what is necessary to use the service as intended.
        </p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">9. Changes to These Terms</h3>
        <p>
          We may update these Terms from time to time. Material changes will be communicated by email
          with at least 30 days' notice. Continued use of the service after the effective date of any
          update constitutes acceptance of the new Terms.
        </p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">10. Governing Law</h3>
        <p>
          These Terms are governed by the laws of <strong>England and Wales</strong>. Any disputes
          shall be subject to the exclusive jurisdiction of the courts of England and Wales.
        </p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">11. Contact</h3>
        <p>
          J Parker Software · Trading as PM Review<br />
          Email:{' '}
          <a href="mailto:hello@pmreview.co.uk" className="text-brand-600 hover:underline">
            hello@pmreview.co.uk
          </a>
        </p>
      </section>
    </div>
  );
}

// ─── Data Processing Agreement ────────────────────────────────────────────────

export function DataProcessingAgreement() {
  return (
    <div className="space-y-6 text-gray-700 text-sm leading-relaxed">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Data Processing Agreement</h2>
        <p className="text-gray-500 text-xs mt-1">Version {CURRENT_LEGAL_VERSION} · Effective April 2026 · Pursuant to UK GDPR Article 28</p>
      </div>

      <p>
        This Data Processing Agreement ("DPA") is entered into between the <strong>School</strong> (data controller)
        and <strong>J Parker Software</strong>, a sole trader trading as PM Review (data processor), together
        the "parties". It forms part of the Terms of Service and governs the processing of personal data
        carried out by J Parker Software on behalf of the School.
      </p>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">1. Definitions</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>"UK GDPR"</strong> means the UK General Data Protection Regulation and the Data Protection Act 2018.</li>
          <li><strong>"Personal Data"</strong> has the meaning given in UK GDPR.</li>
          <li><strong>"Processing"</strong> has the meaning given in UK GDPR.</li>
          <li><strong>"Data Subject"</strong> means the identified or identifiable natural person to whom Personal Data relates.</li>
          <li><strong>"Sub-processor"</strong> means any third party engaged by J Parker Software to process Personal Data on its behalf.</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">2. Subject Matter and Details of Processing</h3>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Subject matter:</strong> Provision of the PM Review performance management platform.</li>
          <li><strong>Nature and purpose:</strong> Storing, displaying and managing staff performance review data on behalf of the School.</li>
          <li>
            <strong>Types of personal data:</strong> Names, email addresses, job titles, role assignments,
            performance review notes and ratings, evidence file uploads, pay progression decisions,
            session tokens and login timestamps.
          </li>
          <li><strong>Categories of data subjects:</strong> School employees (teaching and support staff, line managers, headteachers).</li>
          <li><strong>Duration:</strong> For the term of the agreement plus 30 days after termination.</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">3. Obligations of J Parker Software (Processor)</h3>
        <p>J Parker Software shall:</p>
        <ol className="list-decimal pl-5 space-y-2 mt-2">
          <li>Process Personal Data only on documented instructions from the School, and only for the purposes of providing the PM Review service.</li>
          <li>Ensure that all personnel authorised to process Personal Data are bound by appropriate confidentiality obligations.</li>
          <li>
            Implement appropriate technical and organisational security measures (including encryption
            in transit and at rest, access controls, single-session enforcement, and automatic inactivity timeouts).
          </li>
          <li>Not engage any new sub-processor without informing the School in advance and giving the School the opportunity to object.</li>
          <li>
            Assist the School in responding to Data Subject rights requests (access, erasure, rectification,
            etc.) by providing relevant data extracts upon written request.
          </li>
          <li>Notify the School without undue delay (and within 72 hours where feasible) upon becoming aware of a Personal Data breach.</li>
          <li>Delete all Personal Data within 30 days of termination of the agreement, and confirm deletion in writing upon request.</li>
          <li>Make available to the School all information reasonably necessary to demonstrate compliance with this DPA.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">4. Obligations of the School (Controller)</h3>
        <p>The School shall:</p>
        <ul className="list-disc pl-5 space-y-2 mt-2">
          <li>Ensure it has a valid lawful basis under UK GDPR for using the platform to process staff personal data.</li>
          <li>Ensure staff are informed about the use of the platform in accordance with the School's privacy notice.</li>
          <li>Only upload personal data to the platform that is necessary for performance management purposes.</li>
          <li>Record this DPA in its data processing register.</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">5. Sub-processors</h3>
        <p>The School provides general authorisation for J Parker Software to engage the following sub-processors:</p>
        <div className="mt-3 rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">Sub-processor</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">Purpose</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">Location</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr>
                <td className="px-4 py-2 text-gray-700">Google LLC (Firebase Firestore, Authentication, Storage)</td>
                <td className="px-4 py-2 text-gray-600">Database, authentication, file storage</td>
                <td className="px-4 py-2 text-gray-600">europe-west2 (London, UK)</td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-gray-700">Google LLC (Firebase Hosting)</td>
                <td className="px-4 py-2 text-gray-600">Web application delivery</td>
                <td className="px-4 py-2 text-gray-600">Global CDN (encrypted in transit)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-3">
          All Google services are covered by Google's Data Processing Addendum and Standard Contractual
          Clauses, meeting UK GDPR international transfer requirements. Primary data storage is in
          London, UK (europe-west2 region), meaning no international transfer occurs for at-rest data.
        </p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">6. International Transfers</h3>
        <p>
          All Personal Data is stored at rest in the United Kingdom (Firebase europe-west2, London).
          No transfers of Personal Data outside the UK or EEA are made for data-at-rest. Content
          delivery infrastructure may serve the web application from global edge locations, but no
          Personal Data is stored at those locations.
        </p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">7. Security Measures</h3>
        <p>Technical and organisational measures implemented include:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>TLS 1.2+ encryption for all data in transit</li>
          <li>AES-256 encryption for all data at rest (Firebase)</li>
          <li>Role-based access control enforced at the database rules level</li>
          <li>Multi-tenant isolation — schools cannot access each other's data</li>
          <li>Single-session enforcement — one active login per user at a time</li>
          <li>Automatic session timeout after 5 minutes of inactivity</li>
          <li>Password reset via secure email link only</li>
        </ul>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">8. Governing Law</h3>
        <p>
          This DPA is governed by the laws of <strong>England and Wales</strong> and is subject to
          the jurisdiction of the courts of England and Wales.
        </p>
      </section>

      <section>
        <h3 className="font-semibold text-gray-900 mb-2">9. Contact for Data Protection Matters</h3>
        <p>
          J Parker Software · Trading as PM Review<br />
          Email:{' '}
          <a href="mailto:hello@pmreview.co.uk" className="text-brand-600 hover:underline">
            hello@pmreview.co.uk
          </a>
        </p>
      </section>
    </div>
  );
}
